import { usePathname } from 'next/navigation'
import styled from 'styled-components'

import InfoTooltip from '@/app/components/InfoToolTip'
import SubsectionTitle from '@/app/components/SubsectionTitle'
import TwitterLink from '@/app/components/TwitterLink'
import useWindowDimensions from '@/app/hooks/useWindowDimensions'
import { useEnvironment } from '@/app/providers/EnvironmentProvider'
import { colors, spaces } from '@/config/global-parameters'
import { millionFormatter, percentFormatter } from '@/helpers/formatters'
import Blancos from '@/images/Blancos'
import Nulos from '@/images/Nulos'
import Participacion from '@/images/Participacion'
import Validos from '@/images/Validos'

import { IResultadosConsejerosEnv } from '../../consejeros/page'
import ParticipationTable from './ParticipationTable'
import { IParticipacion } from './ParticipationTable/types'

interface IParticipationSection {
  circumscriptionId?: string | number
  communeId?: string
  participacion?: IParticipacion
  twitterPosition?: 'up' | 'down'
}

const ParticipacionSummary = () => {
  const { isBigScreen } = useWindowDimensions()
  const logoWidth = isBigScreen ? 30 : 15
  const titleSize = 12
  const total = 15150571

  const {
    liveData: { resultadosBlancosNulos },
  } = useEnvironment<IResultadosConsejerosEnv>()

  if (!resultadosBlancosNulos) return null

  const { blancos, nulos, validos } = resultadosBlancosNulos
  const votosTotalesActuales = blancos + nulos + validos

  return (
    <ParticipacionBox>
      <ColumnContainer>
        <RowContainer>
          <Validos width={logoWidth} />
          <ColumnContainer gap>
            <Title fontSize={titleSize}>VÁLIDOS</Title>
            <FormattedNumber>
              {percentFormatter.format(Number(validos / votosTotalesActuales) || 0)}
            </FormattedNumber>
            <FormattedNumber>{millionFormatter.format(Number(validos) || 0)}</FormattedNumber>
          </ColumnContainer>
        </RowContainer>
      </ColumnContainer>

      <ColumnContainer>
        <RowContainer>
          <Blancos width={logoWidth} />
          <ColumnContainer gap>
            <Title fontSize={titleSize}>BLANCOS</Title>
            <FormattedNumber>
              {percentFormatter.format(Number(blancos / votosTotalesActuales) || 0)}
            </FormattedNumber>
            <FormattedNumber>{millionFormatter.format(Number(blancos) || 0)}</FormattedNumber>
          </ColumnContainer>
        </RowContainer>
      </ColumnContainer>

      <ColumnContainer>
        <RowContainer>
          <Nulos width={logoWidth} />
          <ColumnContainer gap>
            <Title fontSize={titleSize}>NULOS</Title>
            <FormattedNumber>
              {percentFormatter.format(Number(nulos / votosTotalesActuales) || 0)}
            </FormattedNumber>
            <FormattedNumber>{millionFormatter.format(Number(nulos) || 0)}</FormattedNumber>
          </ColumnContainer>
        </RowContainer>
      </ColumnContainer>

      <ColumnContainer>
        <RowContainer>
          <Participacion width={logoWidth} />
          <ColumnContainer gap>
            <Title fontSize={titleSize}>PARTICIPACIÓN</Title>
            <FormattedNumber>
              {percentFormatter.format(Number(votosTotalesActuales / total) || 0)}
            </FormattedNumber>
            <FormattedNumber>
              {millionFormatter.format(Number(votosTotalesActuales) || 0)}
            </FormattedNumber>
          </ColumnContainer>
        </RowContainer>
      </ColumnContainer>
    </ParticipacionBox>
  )
}

const ParticipationSection = ({
  circumscriptionId,
  communeId,
  participacion,
  twitterPosition = 'down',
}: IParticipationSection) => {
  const pathname = usePathname()
  const isInCircunscription = pathname.includes('circunscripciones')
  const shouldDisplayBlancosNulos = !isInCircunscription
  return (
    <Wrapper>
      <TitleWrapper>
        <SubsectionTitle>Participación</SubsectionTitle>
        <InfoTooltip
          color={colors.colorBlack}
          id="participation-tooltip"
          message={
            <PopoverMessage>
              Es la suma de todos los votos válidamente emitidos, nulos y blancos
            </PopoverMessage>
          }
        />
      </TitleWrapper>
      {shouldDisplayBlancosNulos && <ParticipacionSummary />}
      {twitterPosition === 'up' && <Twitter />}
      {participacion && (
        <ParticipationBox>
          <ParticipationTable
            circumscriptionId={circumscriptionId}
            communeId={communeId}
            participacion={participacion}
          />
        </ParticipationBox>
      )}
      {twitterPosition === 'down' && <Twitter />}
    </Wrapper>
  )
}

const FormattedNumber = styled.div`
  font-size: 12px;
`

const PopoverMessage = styled.div`
  @media (max-width: ${spaces.mobileBreakpoint}) {
    max-width: 250px;
    text-align: center;
  }
`

const ParticipationBox = styled.div`
  overflow-y: hidden;
`

const ColumnContainer = styled.div<{ gap?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-top: ${({ gap }) => (gap ? -6 : 0)}px;
`

const RowContainer = styled.div<{ gap?: number }>`
  display: flex;
  gap: ${({ gap }) => gap || 4}px;
`

const Title = styled.div<{ fontSize?: number }>`
  font-size: ${({ fontSize }) => fontSize || 12}px;
  font-weight: 700;
`

const ParticipacionBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  width: 100%;
  height: 100px;
  border: 1px solid #cfd2df;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spaces.s};
  width: 100%;
  overflow-y: hidden;
  margin-bottom: ${spaces.m};
`

const TitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: ${spaces.xs};
  align-items: center;
  @media (max-width: ${spaces.tabletBreakpoint}) {
    padding-bottom: ${spaces.s};
  }
`

const Twitter = styled(TwitterLink)`
  align-self: flex-end;
`

export default ParticipationSection
